import React, { useEffect, useState } from 'react';
import { logInfo } from '../shared/utils/logging';
import { useScript } from '../hooks';
import {
  convertPrice,
  getCurrency,
} from '../shared/product-page-placement/currency';
import { useSharedContext } from './SharedContext';

type Props = {
  children: React.ReactNode;
  currency: string;
};

interface CurrencyContextShape {
  currency?: string;
  convertPrice: (price: number) => number;
}

const defaultCurrencyContext: CurrencyContextShape = {
  convertPrice: (price) => price,
};

const CurrencyContext = React.createContext(defaultCurrencyContext);

export const CurrencyContextProvider = ({
  children,
}: Pick<Props, 'children'>) => {
  const selectedCurrency = getCurrency();
  const { runningAppName } = useSharedContext();

  // these are needed to track changes via hooks
  const storeActiveCurrency = window.Shopify.currency.active;
  const windowCurrency = window.Currency;

  const [shouldLoadCurrencies, setShouldLoadCurrencies] = useState(false);

  useEffect(() => {
    const shouldLoad =
      storeActiveCurrency !== selectedCurrency.currency && !windowCurrency;
    if (shouldLoad) {
      logInfo(
        `Found currency conversion app and Currency is not present. Loading... Selected currency: ${selectedCurrency.currency}`,
        runningAppName,
      );
      setShouldLoadCurrencies(true);
    }
  }, [
    selectedCurrency.currency,
    storeActiveCurrency,
    windowCurrency,
    runningAppName,
  ]);

  useScript(
    'https://cdn.shopify.com/s/javascripts/currencies.js',
    shouldLoadCurrencies,
  );

  return (
    <CurrencyContext.Provider value={{ convertPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
